import type {
  OpsAnnounceAudienceMode,
  OpsAnnounceNotificationRequest,
  OpsAnnounceNotificationResponse,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrapper around `POST /v1/ops/notifications/announce` (#1396
 * follow-up) — the operator-authored `kind:"system"` broadcast.
 *
 * ops-api gates this on `ops-admin` alone (narrower than the ticket
 * surface next door), so a support-only session surfaces 403 FORBIDDEN,
 * which `opsApiRequest` throws as `OpsApiError`. snake_case on the wire,
 * like every other operator surface.
 *
 * Optional fields are OMITTED rather than sent as null when unset: the
 * platform route rejects an unrecognised `severity` instead of defaulting
 * it, so "absent" and "wrong" must stay distinct all the way down.
 *
 * There is no audience CATEGORY parameter — no brand, no segment (#1946). The
 * route rejects `primary_brand` outright, so sending one would fail the whole
 * broadcast rather than narrow it.
 *
 * `test_recipients` (#1965) is the one narrowing, and it is the ONE optional field
 * that does not follow the omit-when-unset rule, because for it the rule points
 * the wrong way. The platform route refuses `[]` rather than reading it as "no
 * filter"; dropping the key on an empty array would turn that refusal into a
 * perfectly legal FLEET-WIDE announcement, unrecallable, on a request whose
 * caller believed it had named its recipients. So an absent field and an empty
 * one are relayed as the two different things they are: omitted means the
 * fleet, `[]` is sent as `[]` and comes back a 400. `severity` may collapse
 * "absent" into a default; audience may not.
 *
 * `mode` in the response is narrowed rather than trusted — see
 * `AnnounceNotificationResult`.
 */

const ANNOUNCE_PATH = '/v1/ops/notifications/announce';

/**
 * The announce response as the console may ACTUALLY receive it.
 *
 * The contract declares `mode` required, and the platform this ships with
 * always sends it. But the field exists precisely to detect a platform that
 * predates #1965 — and that platform sends no `mode` at all. Typing the read
 * side as required would make the runtime case the type system says cannot
 * happen the very case the check is for, and an "equivalent" simplification of
 * `mode !== 'whitelist'` into `mode === 'fleet'` would then delete the whole
 * detection while the compiler nods along.
 */
export interface AnnounceNotificationResult
  extends Omit<OpsAnnounceNotificationResponse, 'mode'> {
  /** `undefined` when the platform answered without a recognisable mode. */
  readonly mode: OpsAnnounceAudienceMode | undefined;
}

/** `mode` typed as it arrives: unvalidated, and possibly not there at all. */
type AnnounceWireResponse = Omit<OpsAnnounceNotificationResponse, 'mode'> & {
  readonly mode?: unknown;
};

function readAudienceMode(raw: unknown): OpsAnnounceAudienceMode | undefined {
  return raw === 'fleet' || raw === 'whitelist' ? raw : undefined;
}

export async function announceNotification(
  input: OpsAnnounceNotificationRequest,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<AnnounceNotificationResult> {
  const body: Record<string, unknown> = {
    slug: input.slug,
    title: input.title,
    description: input.description,
  };
  if (input.severity !== undefined) {
    body.severity = input.severity;
  }
  // `!== undefined` and nothing more: see the header note. An `.length > 0`
  // guard here would silently promote a scoped send to a fleet broadcast.
  if (input.test_recipients !== undefined) {
    body.test_recipients = [...input.test_recipients];
  }
  const response = await opsApiRequest<AnnounceWireResponse>(
    ANNOUNCE_PATH,
    { method: 'POST', body, signal },
    fetchImpl,
  );
  return {
    fingerprint: response.fingerprint,
    mode: readAudienceMode(response.mode),
    orgs_targeted: response.orgs_targeted,
    created: response.created,
    resurfaced: response.resurfaced,
  };
}
